/**
 * Aerial and Scissor Lift Inspection Component
 * ============================================
 * 
 * Individual form component for daily aerial and scissor lift equipment inspections.
 * 
 * Base Components:
 * - EXTENDS BaseFormComponent<AerialAndScissorLift> for form logic inheritance
 * - USES FormLayoutComponent for step navigation UI (in template)
 * 
 * Form Configuration:
 * - Form structure, steps, and validation rules are primarily defined in the form model
 * - Signature and upload requirements are configured via form metadata
 * - Component implements abstract methods: initializeForm() and getFormData()
 * 
 * Key Functionality:
 * - Creates nested form groups for logical organization (basic, checkItems, groundControl, platformControl)
 * - Auto-populates inspector field with current user name
 * - Implements step validation with signature requirements for final step
 * - Flattens nested form structure for backend submission
 * - Provides type-safe form group getters for template binding
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../form-layout.component';
import { AerialAndScissorLift } from '../../../../../models/forms/aerial-and-scissor-lift/aerial-and-scissor-lift.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';

@Component({
  selector: 'app-daily-aerial-and-scissor-lift',
  templateUrl: './aerial-and-scissor-lift.component.html',
  styleUrls: ['./aerial-and-scissor-lift.component.css']
})
export class AerialAndScissorLiftComponent extends BaseFormComponent<AerialAndScissorLift> implements OnInit {

  // ============================================================================
  // FORM IDENTITY AND STRUCTURE
  // ============================================================================
  // These properties define the form's identity and are required by BaseFormComponent
  
  /** 
   * Form type identifier for routing and submission.
   * Retrieved from the model's static method for consistency.
   */
  override formType = AerialAndScissorLift.getFormInfo().formType;
  
  /** 
   * Array of form steps defining the inspection workflow.
   * Retrieved from the model's static method for consistency.
   */
  override steps: FormStep[] = AerialAndScissorLift.getFormSteps();
  
  /** 
   * Form data model instance for initialization and data handling.
   * New instance created for each form submission.
   */
  override model = new AerialAndScissorLift();
  
  /** 
   * Current user's name for auto-populating inspector field.
   * Retrieved from authentication service during initialization.
   */
  userName: string = '';
  
  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    // Call parent constructor to initialize base functionality
    super(fb, formService, router, entraService, attachmentService, toastService, signatureService, screenSizeService);

    // Load form metadata and set user information
    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    this.userName = this.entraService.userName();
  }

  /**
   * Lifecycle hook that initializes the component.
   * Calls parent initialization to set up form structure and validation.
   */
  override ngOnInit(): void {
    super.ngOnInit();
  }

  // ============================================================================
  // FORM INITIALIZATION
  // ============================================================================
  // Implementation of abstract method required by BaseFormComponent
  
  /**
   * Initializes the form with its controls and validators.
   * Creates the form structure with nested form groups for logical organization.
   * Sets initial values and applies required validators to all mandatory fields.
   * 
   * Form Structure:
   * - basic: Equipment identification and inspector information
   * - checkItems: Equipment condition and safety checks
   * - groundControlStation: Ground-level operation controls
   * - platformControlStation: Platform-level controls and final inspection
   * 
   * Validation:
   * - All fields except 'otherRepairs' are required
   * - Inspector field auto-populates with current user
   * - Date field captures inspection timestamp
   */
  protected override initializeForm(): void {
    this.form = this.fb.group({
      // Basic equipment and inspection information
      basic: this.fb.group({
        location: [this.model.location, Validators.required],
        unitNumber: [this.model.unitNumber, Validators.required],
        typeOfEquipment: [this.model.typeOfEquipment, Validators.required],
        jobNumber: [this.model.jobNumber, Validators.required],
        inspectedBy: [this.userName, Validators.required],
        date: [this.model.date, Validators.required],
      }),
      
      // Equipment condition and safety checks
      checkItems: this.fb.group({
        fuelLevel: [this.model.fuelLevel, Validators.required],
        engineOilLevel: [this.model.engineOilLevel, Validators.required],
        hydraulicSystemLevel: [this.model.hydraulicSystemLevel, Validators.required],
        batteryCondition: [this.model.batteryCondition, Validators.required],
        tiresCondition: [this.model.tiresCondition, Validators.required],
        platformStructure: [this.model.platformStructure, Validators.required],
        instructionPlacards: [this.model.instructionPlacards, Validators.required],
        eyeWashFireExtinguisher: [this.model.eyeWashFireExtinguisher, Validators.required],
        tiltAlarm: [this.model.tiltAlarm, Validators.required],
      }),
      
      // Ground control station operation verification
      groundControlStation: this.fb.group({
        groundRaiseLowerPlatform: [this.model.groundRaiseLowerPlatform, Validators.required],
        groundAuxiliaryPower: [this.model.groundAuxiliaryPower, Validators.required],
        groundTelescopeOutIn: [this.model.groundTelescopeOutIn, Validators.required],
        groundSwingRightLeft: [this.model.groundSwingRightLeft, Validators.required],
      }),
      
      // Platform control station operation verification
      platformControlStation: this.fb.group({
        platformSafetyHarness: [this.model.platformSafetyHarness, Validators.required],
        platformTelescopeOutIn: [this.model.platformTelescopeOutIn, Validators.required],
        platformRaiseLowerPlatform: [this.model.platformRaiseLowerPlatform, Validators.required],
        platformSwingRightLeft: [this.model.platformSwingRightLeft, Validators.required],
        extendLevelOutriggers: [this.model.extendLevelOutriggers, Validators.required],
        driveMachineForwardReverse: [this.model.driveMachineForwardReverse, Validators.required],
        platformAuxiliaryPower: [this.model.platformAuxiliaryPower, Validators.required],
        areaInspected: [this.model.areaInspected, Validators.required],
        otherRepairs: [this.model.otherRepairs], // Not required - optional field
      })
    });
  }

  // ============================================================================
  // FORM DATA EXTRACTION
  // ============================================================================
  // Implementation of abstract method required by BaseFormComponent
  
  /**
   * Returns the form data for submission.
   * Flattens the nested form groups into a single object structure
   * that matches the expected model format for backend submission.
   * 
   * Data Processing:
   * 1. Extract values from all form groups
   * 2. Flatten nested structure into single object
   * 3. Create new model instance with flattened data
   * 4. Update model with attachment and signature information
   * 
   * @returns AerialAndScissorLift model instance ready for submission
   */
  protected override getFormData(): AerialAndScissorLift {
    const formValue = this.form.value;
    
    // Flatten the nested form groups into a single object
    const flattenedData = {
      ...formValue.basic,
      ...formValue.checkItems,
      ...formValue.groundControlStation,
      ...formValue.platformControlStation
    };
    
    // Create new model instance with flattened data
    const aerialAndScissorLift = new AerialAndScissorLift(flattenedData);
    
    // Update model with any attachments and signatures
    const updatedAerialAndScissorLift = this.updateModelWithAttachments(aerialAndScissorLift);
    
    return updatedAerialAndScissorLift;
  }

  // ============================================================================
  // STEP VALIDATION SETUP
  // ============================================================================
  // Custom validation rules for each inspection step
  
  /**
   * Sets up validation rules for each step of the daily aerial and scissor lift inspection form.
   * Each step has its own validation requirements and must be visited to be considered valid.
   * 
   * Validation Rules:
   * - Basic: Validates date, location, unit number, equipment type, job number, and inspector fields
   * - Check Items: Validates all equipment condition and safety check fields
   * - Ground Control: Validates ground control station operation fields
   * - Platform Control: Validates platform control station operation fields and requires signature if needed
   * 
   * Signature Requirements:
   * - Platform Control step requires inspector signature (index 0 in signature requirements)
   * - Signature validation ensures proper documentation of final inspection
   */
  protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true (development/testing)
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    
    // Define signature requirement index for platform control step
    const SIGNATURE_REQUIREMENT_INDEX: number = 0;

    // Set up validation rules for each step using the form groups directly
    this.setupStepValidation('basic', this.basicFormGroup);
    this.setupStepValidation('check-items', this.checkItemsFormGroup);
    this.setupStepValidation('ground-control-station', this.groundControlFormGroup);
    
    // Platform control step requires signature validation
    this.setupStepValidation('platform-control-station', this.platformControlFormGroup, { 
      signatureIndex: [SIGNATURE_REQUIREMENT_INDEX] 
    });
  }

  // ============================================================================
  // FORM GROUP GETTERS
  // ============================================================================
  // Convenience getters for accessing nested form groups
  
  /**
   * Getter for the basic information form group.
   * Provides type-safe access to basic form controls.
   * 
   * @returns FormGroup containing basic equipment and inspection information
   */
  get basicFormGroup(): FormGroup {
    return this.form.get('basic') as FormGroup;
  }

  /**
   * Getter for the check items form group.
   * Provides type-safe access to equipment condition controls.
   * 
   * @returns FormGroup containing equipment condition and safety checks
   */
  get checkItemsFormGroup(): FormGroup {
    return this.form.get('checkItems') as FormGroup;
  }

  /**
   * Getter for the ground control station form group.
   * Provides type-safe access to ground operation controls.
   * 
   * @returns FormGroup containing ground control station operation fields
   */
  get groundControlFormGroup(): FormGroup {
    return this.form.get('groundControlStation') as FormGroup;
  }

  /**
   * Getter for the platform control station form group.
   * Provides type-safe access to platform operation controls.
   * 
   * @returns FormGroup containing platform control station operation fields
   */
  get platformControlFormGroup(): FormGroup {
    return this.form.get('platformControlStation') as FormGroup;
  }
}